import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  forwardRef
} from "@angular/core";
import { FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatMenuModule, MenuPositionX, MenuPositionY } from "@angular/material/menu";
import { NgbAlert, NgbTooltipModule } from "@ng-bootstrap/ng-bootstrap";
import { TranslocoModule } from "@ngneat/transloco";
import { BehaviorSubject, Observable, Subject, of } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
  startWith
} from "rxjs/operators";
import { SimplebarAngularModule } from "simplebar-angular";
import { HighlightPipe } from "../../pipes/highlight.pipe";
import { i18nScope } from "../../utils";
import { FilterSelectComponentBase } from "../base/filter-select-component-base";
import { IFilterSelectItem } from "../i-filter-select-item";

/**
 * Autocomplete filter component for async search with debouncing.
 * @example
 * <lib-filter-select-autocomplete
 *   [formControl]="control"
 *   [searchFn]="searchFunction"
 *   [placeholder]="'Search...'"
 *   [minSearchLength]="2"
 *   [debounceMs]="300">
 * </lib-filter-select-autocomplete>
 */
@Component({
  selector: "lib-filter-select-autocomplete",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatMenuModule,
    NgbTooltipModule,
    NgbAlert,
    SimplebarAngularModule,
    TranslocoModule,
    HighlightPipe
  ],
  templateUrl: "./filter-select-autocomplete.component.html",
  styleUrls: ["./filter-select-autocomplete.component.scss", "../filter-select-common.scss"],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => FilterSelectAutocompleteComponent), multi: true }],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterSelectAutocompleteComponent<TValue = number>
  extends FilterSelectComponentBase<TValue, TValue>
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() searchFn!: (query: string) => Observable<IFilterSelectItem<TValue>[]>;
  @Input() minSearchLength = 3;
  @Input() debounceMs = 300;
  @Input() showItemDescription = false;
  @Input() disabled = false;
  @Input() override label = "";
  @Input() override placeholder = "";
  @Input() override xPosition: MenuPositionX = "after";
  @Input() override yPosition: MenuPositionY = "below";

  @ViewChild("menuContent") menuContent!: ElementRef;
  @ViewChild("menuTriggerButton", { read: ElementRef }) menuTriggerButton!: ElementRef;
  @ViewChild("searchInput") searchInput!: ElementRef;

  selectedItem: IFilterSelectItem<TValue> | null = null;
  searchResults$!: Observable<IFilterSelectItem<TValue>[]>;
  isSearching$ = new BehaviorSubject<boolean>(false);

  private searchSubject$ = new BehaviorSubject<string>("");
  private destroy$ = new Subject<void>();

  readonly menuPaddingX = 8;
  readonly i18nScope = i18nScope;

  constructor() {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initSearchStream();
  }

  ngAfterViewInit(): void {
    this.adjustMenuPosition(this.menuTriggerButton, this.itemsWidthPx, this.menuPaddingX);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject$.complete();
    this.isSearching$.complete();
  }

  private initSearchStream(): void {
    this.searchResults$ = this.searchSubject$.pipe(
      debounceTime(this.debounceMs),
      distinctUntilChanged(),
      filter((query) => query.length >= this.minSearchLength),
      tap(() => this.isSearching$.next(true)),
      switchMap((query) =>
        this.searchFn(query).pipe(
          catchError(() => of([])),
          finalize(() => this.isSearching$.next(false))
        )
      ),
      shareReplay(1),
      takeUntil(this.destroy$)
    );
  }

  menuOpened(): void {
    const menuContentWidth = this.menuContent.nativeElement.offsetWidth;
    let currentElement = this.menuContent.nativeElement;

    if (currentElement && !currentElement.style.width) {
      while (currentElement) {
        if (currentElement.classList?.contains("cdk-overlay-pane")) {
          currentElement.style.width = `${menuContentWidth}px`;
          break;
        }
        currentElement = currentElement.parentElement;
      }
    }
  }

  menuClosed(): void {
    this.filterText = "";
  }

  onSearchInput(value: string): void {
    this.filterText = value;
    this.searchSubject$.next(value);
  }

  selectItem(item: IFilterSelectItem<TValue>): void {
    this.selectedItem = item;
    this.formControl.setValue(item.value);

    this.closeMenu();
    this.filterText = "";
  }

  clearSelection(): void {
    this.selectedItem = null;
    this.formControl.setValue(null as any);
    this.closeMenu();
  }

  protected updateSelectedFromControl(): void {}

  get displayText(): string {
    return this.selectedItem?.title || this.label;
  }

  get showClearButton(): boolean {
    return !!this.selectedItem && this.canClear;
  }
}
